/**
 * Id de cada intento de formulario de ingreso (login, registro, inscripción, checkout, cambio de
 * teléfono). Viaja tres veces con el mismo valor y por eso tiene que ser un UUID de verdad:
 *
 * - como `cData` del widget de Turnstile (Cloudflare lo devuelve al verificar el token),
 * - como `attemptId` en el body de send-otp (la API sólo lo guarda si es UUID),
 * - como `attemptId` en los beacons de `/api/auth/bot-event` (el schema exige formato uuid y un
 *   valor inválido haría rechazar el evento entero).
 *
 * Con ese id el panel une el pedido de código, el pedido del clic y lo que reportó el navegador.
 */

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isAttemptId(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function hex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** UUID v4 armado a mano a partir de 16 bytes, para navegadores sin `crypto.randomUUID`. */
function uuidFromBytes(bytes: Uint8Array) {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = hex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function newAttemptId(): string {
  const cryptoApi = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  try {
    // Sólo existe en contextos seguros (HTTPS) y navegadores de 2021 en adelante.
    if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  } catch {
    // Algunos WebView viejos lo exponen pero tiran: se cae al armado manual.
  }
  const bytes = new Uint8Array(16);
  try {
    if (typeof cryptoApi?.getRandomValues === "function") {
      return uuidFromBytes(cryptoApi.getRandomValues(bytes));
    }
  } catch {
    // Sin crypto: el id es para correlacionar, no un secreto, así que alcanza con Math.random.
  }
  for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return uuidFromBytes(bytes);
}
